import {
  RotinaArquivoBase,
  type TParametrosRotinaArquivo,
} from '@/domain/simulador/factories/concrete/horizontal/sacocv/rotina-arquivo-base'
import {
  SimulacaoSacocVBase,
  type TGruposParcelaBase,
} from '@/domain/simulador/factories/concrete/horizontal/sacocv/simulacao-sacocv-base'
import type {
  TSimulacaoCalculoPrazo,
  TSimulacaoCalculoRotinaArquivo,
  TSimulacaoCalculoValoresTotais,
  TSimulacaoFluxoPagamento,
} from '@/domain/simulador/factories/types/simulacao-calculo'
import { GerenciadorEntradasBase } from '@/domain/simulador/gerenciador-entradas-base'
import { GerenciadorIntermediariasBase } from '@/domain/simulador/gerenciador-intermediarias-base'
import { GerenciadorMensaisBase } from '@/domain/simulador/gerenciador-mensais-base'
import { GerenciadorPosConclusaoBase } from '@/domain/simulador/gerenciador-pos-conclusao-base'
import { GerenciadorSaldoBase } from '@/domain/simulador/gerenciador-saldo-base'
import type {
  TParametrosDinamicosSimulacaoSimulador,
  TSimulacaoGrupoSaldoBase,
  TSimulacaoSchema,
} from '@/domain/simulador/types/simulacao.type'
import { CalculadorFluxoDePagamentoBase } from '@/features/simulador/brasil/domain/calculador-fluxo-pagamento-base'
import { CalculadorPrazoBase } from '@/features/simulador/brasil/domain/calculador-prazo-base'
import type { TGetSimulacaoDuplicarResponse } from '@/types/simulacao'
import { CalculadoraDeValoresTotaisBase } from '@/utils/calculadora-valores-totais'
import { GruposParcelasApiMapper } from '@/utils/grupos-parcelas-api.mapper'

type TGruposParcelaSaldo = TGruposParcelaBase & {
  saldo: TSimulacaoGrupoSaldoBase
}

export class SimulacaoSacocVSaldo extends SimulacaoSacocVBase {
  readonly entradas = new GerenciadorEntradasBase()
  readonly mensais = new GerenciadorMensaisBase()
  readonly intermediarias = new GerenciadorIntermediariasBase()
  readonly posConclusao = new GerenciadorPosConclusaoBase()
  readonly saldo = new GerenciadorSaldoBase()
  readonly tipoDeAmortizacao = 'SacocV' as const
  readonly formaDePagamento = 'Saldo' as const

  protected criarGruposParcela(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TGruposParcelaSaldo {
    const { condicaoDePreco, tabelaDePreco } = parametrosDinamicos

    const datasSinais = [
      condicaoDePreco.dataSinal1,
      condicaoDePreco.dataSinal2,
      condicaoDePreco.dataSinal3,
      condicaoDePreco.dataSinal4,
    ].filter((dataSinal): dataSinal is string => Boolean(dataSinal))

    const mensais = this.mensais.criarGrupoInicial(
      condicaoDePreco.dataPrimeiraMensal,
      condicaoDePreco.dataUltimaMensal,
      tabelaDePreco.valorBaseMensais,
    )

    const validacaoPosConclusao = this.posConclusao.deveGerarPosConclusao(
      condicaoDePreco.previsaoDeEntregaDoBloco,
      this.dataSimulacao,
    )

    const entradas = this.entradas.criarGrupoInicial(datasSinais, tabelaDePreco.valorBaseSinal, {
      data: condicaoDePreco.dataAto,
      valor: tabelaDePreco.valorBaseAto,
    })

    return {
      entradas,
      mensais,
      intermediarias: this.intermediarias.criarGrupoInicial(
        condicaoDePreco.dataDasIntermediarias,
        tabelaDePreco.valorBaseIntermediarias,
        condicaoDePreco.previsaoDeEntregaDoBloco,
      ),
      saldo: this.saldo.criar(condicaoDePreco.dataSaldo, tabelaDePreco.valorBaseSaldo),
      posConclusao: validacaoPosConclusao.sucesso
        ? this.posConclusao.criarGrupo(
            tabelaDePreco.valorBaseMensais,
            condicaoDePreco.dataSaldo,
            condicaoDePreco.previsaoDeEntregaDoBloco,
            this.dataSimulacao,
          )
        : undefined,
    }
  }

  calcularRotinaArquivo(grupos: TSimulacaoCalculoRotinaArquivo, parametrosRotinaArquivo: TParametrosRotinaArquivo) {
    const rotinaArquivo = new RotinaArquivoBase(parametrosRotinaArquivo)
    return rotinaArquivo.calcular(grupos)
  }

  calcularValoresTotais(grupos: TSimulacaoCalculoValoresTotais, valorTabela = 0) {
    return new CalculadoraDeValoresTotaisBase(grupos, valorTabela)
  }

  calcularFluxoDePagamento(grupos: TSimulacaoFluxoPagamento) {
    return new CalculadorFluxoDePagamentoBase(
      grupos.entradas,
      grupos.mensais,
      grupos.intermediarias,
      grupos.saldo,
      grupos.posConclusao,
    )
  }

  calcularPrazo(simulacao: TSimulacaoCalculoPrazo) {
    return new CalculadorPrazoBase(simulacao)
  }

  criar(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TSimulacaoSchema<'SacocV'> {
    const grupos = this.criarGruposParcela(parametrosDinamicos)
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)
    return {
      id: crypto.randomUUID(),
      dataSimulacao: this.dataSimulacao,
      formaDePagamento: 'Saldo',
      ...simulacaoBase,
      ...grupos,
      situacao: { status: 'EmAndamento' as const, dados: undefined },
    }
  }

  duplicar(
    simulacao: TGetSimulacaoDuplicarResponse,
    parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador,
  ): TSimulacaoSchema<'SacocV', 'Saldo'> {
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)
    const { entradas, intermediarias, mensais, posConclusao, saldo } = new GruposParcelasApiMapper(
      simulacao,
    ).buildGruposParcelas()

    return {
      id: crypto.randomUUID(),
      simulacaoAnteriorId: simulacao.id,
      dataSimulacao: this.dataSimulacao,
      formaDePagamento: this.formaDePagamento,
      ...simulacaoBase,
      entradas,
      mensais,
      intermediarias,
      saldo: saldo!,
      posConclusao,
      situacao: { status: 'EmAndamento' as const, dados: undefined },
    }
  }

  recalcular(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador) {
    const grupos = this.criarGruposParcela(parametrosDinamicos)
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)

    return {
      ...simulacaoBase,
      ...grupos,
    } as const
  }
}
